import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';
import axios from 'axios';

const DynamicPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [pageContent, setPageContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPageContent();
  }, [slug]);

  const fetchPageContent = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/cms/page/${slug}`);
      console.log('Page content:', response.data);
      
      if (response.data && !response.data.error) {
        setPageContent(response.data);
        setError(null);
      } else {
        setError('Page not found');
      }
    } catch (error) {
      console.error('Error fetching page content:', error);
      setError('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !pageContent) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="bg-blue-600 p-2 rounded-lg">
                <span className="text-white text-xl font-bold">CK</span>
              </div>
              <span className="text-xl font-bold text-gray-800">College Kampus</span>
            </div>
            <div className="flex items-center gap-6">
              <button onClick={() => navigate('/')} className="text-gray-600 hover:text-blue-600 transition">
                Home
              </button>
              <button onClick={() => navigate('/about-us')} className="text-gray-600 hover:text-blue-600 transition">
                About Us
              </button>
              <Button onClick={() => navigate('/user-login')}>Login</Button>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Page Not Found</h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </div>
        <Footer />
      </div>
    );
  }

  const sections = pageContent.sections || {};

  const renderSection = (key, section) => {
    // Hero section
    if (key === 'hero' && section.title) {
      return (
        <section key={key} className="py-20 px-4">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-4">
              {section.title}
            </h1>
            {section.subtitle && (
              <p className="text-xl text-gray-600 mb-6">
                {section.subtitle}
              </p>
            )}
            {section.description && (
              <p className="text-lg text-gray-500">
                {section.description}
              </p>
            )}
          </div>
        </section>
      );
    }

    // Text block
    if (section.type === 'textBlock') {
      return (
        <section key={key} className="py-16 px-4 bg-white">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {section.title}
            </h2>
            <div className="text-gray-700 text-lg leading-relaxed whitespace-pre-wrap">
              {section.content}
            </div>
          </div>
        </section>
      );
    }

    // Image & Text
    if (section.type === 'imageText') {
      return (
        <section key={key} className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <img 
                  src={section.imageUrl || 'https://via.placeholder.com/400x300'} 
                  alt={section.title}
                  className="rounded-xl shadow-lg w-full"
                />
              </div>
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  {section.title}
                </h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  {section.content}
                </p>
              </div>
            </div>
          </div>
        </section>
      );
    }

    // Features Grid
    if (section.type === 'features' && section.items) {
      return (
        <section key={key} className="py-16 px-4 bg-white">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-4xl font-bold text-center text-gray-900 mb-12">
              {section.title}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {section.items.map((item, index) => (
                <div key={index} className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition">
                  <div className="text-4xl mb-4">{item.icon || '✨'}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {item.title}
                  </h3>
                  <p className="text-gray-600">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    }

    // Call to Action
    if (section.type === 'cta') {
      return (
        <section key={key} className="py-20 px-4 bg-gradient-to-r from-blue-600 to-purple-600">
          <div className="container mx-auto max-w-4xl text-center text-white">
            <h2 className="text-4xl font-bold mb-4">
              {section.title}
            </h2>
            {section.subtitle && (
              <p className="text-xl mb-8 opacity-90">
                {section.subtitle}
              </p>
            )}
            {section.buttonText && (
              <Button 
                size="lg"
                className="bg-white text-blue-600 hover:bg-gray-100"
                onClick={() => window.location.href = section.buttonLink || '#'}
              >
                {section.buttonText}
              </Button>
            )}
          </div>
        </section>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
            <div className="bg-blue-600 p-2 rounded-lg">
              <span className="text-white text-xl font-bold">CK</span>
            </div>
            <span className="text-xl font-bold text-gray-800">College Kampus</span>
          </div>
          <div className="flex items-center gap-6">
            <button onClick={() => navigate('/')} className="text-gray-600 hover:text-blue-600 transition">
              Home
            </button>
            <button onClick={() => navigate('/about-us')} className="text-gray-600 hover:text-blue-600 transition">
              About Us
            </button>
            <Button onClick={() => navigate('/user-login')}>Login</Button>
          </div>
        </div>
      </header>

      {/* Page Sections */}
      {Object.entries(sections).map(([key, section]) => renderSection(key, section))}

      <Footer />
    </div>
  );
};

export default DynamicPage;
